import React from 'react';
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, Legend } from 'recharts';
import { ReportTheme } from '../../types';
import { getChartColor } from './chartUtils';

interface TopGeoLocationsPieChartWidgetProps {
  data: any;
  data_points?: string[];
  report_start_date?: string;
  report_end_date?: string;
  reportTheme: ReportTheme;
}

export const TopGeoLocationsPieChartWidget: React.FC<TopGeoLocationsPieChartWidgetProps> = ({ 
  data, 
  data_points,
  report_start_date,
  report_end_date,
  reportTheme 
}) => {

  // Process the raw API data internally for Top Geo Locations
  const processData = () => {
    // Handle the actual data structure: { success: true, data: {...} }
    if (!data || !data.data || typeof data.data !== 'object') {
      console.warn('TopGeoLocationsPieChartWidget: Invalid data structure - missing data.data object');
      return null;
    }

    const rawData = data.data;
    const entries = Object.entries(rawData);
    
    if (entries.length === 0) {
      console.warn('TopGeoLocationsPieChartWidget: No data entries found');
      return null;
    }

    // Filter data by date range if dates are provided
    let filteredEntries = entries;
    if (report_start_date && report_end_date) {
      const startDate = new Date(report_start_date);
      const endDate = new Date(report_end_date);
      
      filteredEntries = entries.filter(([dateKey, dayData]: [string, any]) => {
        if (!dayData || typeof dayData !== 'object') {
          return false;
        }
        // Convert dateKey (e.g., "20240912") to Date object
        const year = parseInt(dateKey.substring(0, 4));
        const month = parseInt(dateKey.substring(4, 6)) - 1; // Month is 0-indexed
        const day = parseInt(dateKey.substring(6, 8));
        const entryDate = new Date(year, month, day);
        
        return entryDate >= startDate && entryDate <= endDate;
      });
      
      console.log(`TopGeoLocationsPieChartWidget: Filtered ${filteredEntries.length} entries from ${entries.length} total entries for date range ${report_start_date} to ${report_end_date}`);
    }
    
    if (filteredEntries.length === 0) {
      console.warn('TopGeoLocationsPieChartWidget: No data entries found in date range');
      return null;
    }

    // Aggregate country data across all dates
    const countryTotals: { [key: string]: number } = {};

    filteredEntries.forEach(([dateKey, dayData]: [string, any]) => {
      if (!dayData || typeof dayData !== 'object') {
        return;
      }

      // Process each country in the day's data
      Object.entries(dayData).forEach(([countryKey, countryData]: [string, any]) => {
        if (countryData && typeof countryData === 'object') {
          const sessions = countryData.sessions || countryData.count || 0;
          countryTotals[countryKey] = (countryTotals[countryKey] || 0) + sessions;
        }
      });
    });

    // Color variations based on hsl(var(--primary))
    const generatePrimaryVariations = () => {
      // Base primary color (assuming it's a purple-ish color)
      const baseHue = 280; // Purple hue
      const baseSaturation = 80; // High saturation
      const baseLightness = 60; // Medium lightness
      
      return [
        `hsl(${baseHue}, ${baseSaturation}%, ${baseLightness}%)`, // Base primary
        `hsl(${baseHue}, ${baseSaturation}%, ${baseLightness + 15}%)`, // Lighter
        `hsl(${baseHue}, ${baseSaturation}%, ${baseLightness + 25}%)`, // Much lighter
        `hsl(${baseHue}, ${baseSaturation}%, ${baseLightness + 35}%)`, // Very light
        `hsl(${baseHue}, ${Math.max(0, baseSaturation - 20)}%, ${baseLightness + 20}%)`, // Less saturated, lighter
        `hsl(${baseHue}, ${Math.max(0, baseSaturation - 30)}%, ${baseLightness + 30}%)`, // Less saturated, very light
        `hsl(${baseHue}, ${baseSaturation}%, ${Math.max(0, baseLightness - 15)}%)`, // Darker
        `hsl(${baseHue}, ${baseSaturation}%, ${Math.max(0, baseLightness - 25)}%)`, // Much darker
      ];
    };

    const pastelColors = generatePrimaryVariations();

    // Convert to pie chart data format
    const pieData = Object.entries(countryTotals)
      .map(([countryKey, sessions], index) => ({
        name: countryKey,
        value: sessions,
        color: pastelColors[index % pastelColors.length]
      }))
      .sort((a, b) => b.value - a.value) // Sort by sessions descending
      .slice(0, 8); // Top 8 countries

    console.log('TopGeoLocationsPieChartWidget: Country totals:', countryTotals);
    return pieData.length > 0 ? pieData : null;
  };

  const processedData = processData();
  console.log('TopGeoLocationsPieChartWidget processed data:', processedData);
  if (!processedData || processedData.length === 0) {
    return null;
  }

  // Calculate total for percentage calculations
  const total = processedData.reduce((sum, item) => sum + item.value, 0);

  // Custom label function for slices > 30%
  const renderLabel = (entry: any) => {
    const percentage = (entry.value / total) * 100;
    if (percentage > 30) {
      return `${entry.name}`;
    }
    return null;
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsPieChart>
        <Pie
          data={processedData}
          cx="40%"
          cy="50%"
          labelLine={false}
          label={renderLabel}
          outerRadius={80}
          fill={getChartColor(0, reportTheme.theme)}
          dataKey="value"
          stroke="none"
        >
          {processedData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} />
          ))}
        </Pie>
        <Legend 
          layout="vertical"
          align="right"
          verticalAlign="middle"
          formatter={(value, entry) => {
            const total = processedData.reduce((sum, item) => sum + item.value, 0);
            const percentage = entry.payload ? ((entry.payload.value / total) * 100).toFixed(0) : '0';
            return `${value} ${percentage}%`;
          }}
        />
      </RechartsPieChart>
    </ResponsiveContainer>
  );
};
